import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Percent, Plus, Edit } from "lucide-react";

export default function Commissions() {
  const { hasPerm } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [summary, setSummary] = useState(null);
  const { items, total, loading, page, setPage, refetch } = useList("/commissions", {});
  const load = () => { api.get("/commissions/summary").then(({ data }) => setSummary(data)).catch(() => {}); };
  useEffect(() => { api.get("/policies", { params: { limit: 100 } }).then(({ data }) => setPolicies(data.items || [])).catch(() => {}); load(); }, []);
  useEffect(load, [total]);

  const create = async (v) => { await api.post("/commissions", v); refetch(); load(); };
  const edit = async (c, v) => { await api.put(`/commissions/${c.id}`, v); refetch(); load(); };

  const createFields = [
    { name: "policy_id", label: "Policy", type: "select", required: true, options: policies.map((p) => ({ value: p.id, label: `${p.policy_number} — ${p.insurer} (${INR(p.premium)})` })) },
    { name: "commission_rate", label: "Commission Rate %", type: "number", required: true },
    { name: "actual_commission_received", label: "Amount Received (₹)", type: "number" },
    { name: "insurer_statement_reference", label: "Insurer Statement Ref" },
  ];
  const editFields = [
    { name: "actual_commission_received", label: "Amount Received (₹)", type: "number", required: true },
    { name: "insurer_statement_reference", label: "Insurer Statement Ref" },
    { name: "reconciliation_status", label: "Reconciliation", type: "select", options: [{ value: "unreconciled", label: "Unreconciled" }, { value: "reconciled", label: "Reconciled" }, { value: "disputed", label: "Disputed" }] },
  ];
  const columns = [
    { key: "policy_number", header: "Policy", render: (r) => <span className="font-mono text-xs">{r.policy_number}</span> },
    { key: "insurance_company", header: "Insurer", render: (r) => <span className="font-semibold">{r.insurance_company}</span> },
    { key: "policy_premium", header: "Premium", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular">{INR(r.policy_premium)}</span> },
    { key: "commission_rate", header: "Rate", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular">{r.commission_rate}%</span> },
    { key: "expected_commission_amount", header: "Expected", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular text-violet-600 font-semibold">{INR(r.expected_commission_amount)}</span> },
    { key: "actual_commission_received", header: "Received", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular text-emerald-600 font-semibold">{INR(r.actual_commission_received)}</span> },
    { key: "pending_commission", header: "Pending", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular text-amber-600">{INR(r.pending_commission)}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
    ...(hasPerm("commissions:edit") ? [{ key: "actions", header: "", render: (r) => (
      <div onClick={(e) => e.stopPropagation()}>
        <FormDialog title={`Update Commission — ${r.policy_number}`} fields={editFields} initial={{ actual_commission_received: r.actual_commission_received }} onSubmit={(v) => edit(r, v)} submitLabel="Update"
          trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="commission-edit-btn"><Edit className="h-4 w-4" /></Button>} />
      </div>
    ) }] : []),
  ];

  return (
    <div>
      <PageHeader title="Commission & Accounts MIS" subtitle="Policy & insurer-level commission — expected vs actual" icon={Percent}
        actions={hasPerm("commissions:add") && (
          <FormDialog title="Add Commission Entry" description="Duplicate entries per policy are blocked." fields={createFields} onSubmit={create} submitLabel="Add Entry" testId="create-commission-dialog"
            trigger={<Button data-testid="create-commission-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Commission</Button>} />
        )} />
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4"><div className="text-xs text-muted-foreground">Total Expected</div><div className="text-xl font-extrabold tabular text-violet-600 mt-1">{INR(summary.total_expected)}</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Total Received</div><div className="text-xl font-extrabold tabular text-emerald-600 mt-1">{INR(summary.total_received)}</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Total Pending</div><div className="text-xl font-extrabold tabular text-amber-600 mt-1">{INR(summary.total_pending)}</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Entries</div><div className="text-xl font-extrabold tabular mt-1">{summary.count}</div></Card>
        </div>
      )}
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="commission-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
